import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  Grid
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  LocationOn as MapPinIcon,
  Event as CalendarIcon,
  Work as BriefcaseIcon,
  LocalOffer as TagIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Group as UsersIcon,
  OpenInNew as ExternalLinkIcon,
  AttachMoney as DollarSignIcon
} from "@mui/icons-material";

const JobOfferView = ({ data }) => {
  const theme = useTheme();
  if (!data) {
    return null;
  }

  const offer = Array.isArray(data) ? data[0] : data;

  const getStatusConfig = (status) => {
    switch (status) {
      case "OPEN":
        return {
          color: "success",
          icon: <CheckCircleIcon fontSize="small" />,
          label: "Ouverte"
        };
      case "CLOSED":
        return {
          color: "error",
          icon: <XCircleIcon fontSize="small" />,
          label: "Fermée"
        };
      default:
        return {
          color: "default",
          icon: <CheckCircleIcon fontSize="small" />,
          label: status
        };
    }
  };

  const statusConfig = getStatusConfig(offer.status);

  const skillsArray = Array.isArray(offer.skills) 
    ? offer.skills 
    : offer.skills 
      ? offer.skills.split(",").map(s => s.trim()) 
      : [];

  const InfoItem = ({ icon, label, value, iconColor }) => {
    if (!value) return null;
    return (
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
          {React.cloneElement(icon, { sx: { color: iconColor } })}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
              {value}
            </Typography>
          </Box>
        </Box>
      </Grid>
    );
  };

  return (
    <Card elevation={1} sx={{ borderRadius: 4, mb: 2, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: theme.shadows[3] } }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: 3, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
              boxShadow: theme.shadows[2]
            }}>
              <BriefcaseIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {offer.title}
              </Typography>
              {offer.department && (
                <Typography variant="body2" color="text.secondary">
                  {offer.department}
                </Typography>
              )}
            </Box>
          </Box>

          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            color={statusConfig.color}
            variant="outlined"
            sx={{ fontWeight: 600, borderRadius: 2 }}
          />
        </Box>

        {/* Details Grid */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <InfoItem icon={<MapPinIcon />} label="Lieu" value={offer.location} iconColor={theme.palette.error.main} />
          <InfoItem icon={<TagIcon />} label="Type de contrat" value={offer.contract_type} iconColor={theme.palette.info.main} />
          <InfoItem icon={<BriefcaseIcon />} label="Expérience" value={offer.experience_level} iconColor={theme.palette.secondary.main} />
          <InfoItem icon={<DollarSignIcon />} label="Salaire" value={offer.salary} iconColor={theme.palette.success.main} />
          <InfoItem 
            icon={<CalendarIcon />} 
            label="Date limite" 
            value={offer.deadline ? new Date(offer.deadline).toLocaleDateString("fr-FR", { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : null} 
            iconColor={theme.palette.warning.main} 
          />
          <InfoItem icon={<UsersIcon />} label="Candidatures" value={offer.application_count} iconColor={theme.palette.primary.main} />
        </Grid>

        {/* Skills */}
        {skillsArray.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <TagIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Compétences requises
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {skillsArray.map((skill, index) => (
                <Chip
                  key={index}
                  label={skill}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ bgcolor: 'white', fontWeight: 500 }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Action Button */}
        <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            variant="text" 
            color="secondary"
            endIcon={<ExternalLinkIcon />}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Voir l'offre complète
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default JobOfferView;
